import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const PRIMARY = '#4CAF50';

/** 須高於 WpaLoginPage modalOverlay (4000) 等全頁遮罩，避免登入前後被蓋住 */
const Z_OVERLAY = 60000;
const Z_DIALOG = 60001;

/**
 * 每公司 WPA：manifest 由 App（wpaManifestHead）同步；此元件負責 SW 與安裝提示。
 * Android：beforeinstallprompt 整個分頁通常只觸發一次，須以模組變數保存 deferred 事件，
 * 才能在使用者從登入頁導向 Layout 後仍顯示「立即安裝」。
 * iOS：僅顯示 Safari「加入主畫面」說明（無原生 beforeinstallprompt）。
 */

function isRunningAsInstalledPwa() {
  if (typeof window === 'undefined') return false;
  if (window.navigator?.standalone === true) return true;
  const modes = ['standalone', 'fullscreen', 'minimal-ui'];
  return modes.some((mode) => window.matchMedia?.(`(display-mode: ${mode})`)?.matches === true);
}

// --- Singleton：跨路由保留 Android deferred install 事件 ---

let wpaDeferredInstallPrompt = null;
const wpaInstallPromptSubscribers = new Set();

function wpaSubscribeInstallPrompt(cb) {
  wpaInstallPromptSubscribers.add(cb);
  return () => wpaInstallPromptSubscribers.delete(cb);
}

function wpaNotifyInstallPromptSubscribers() {
  wpaInstallPromptSubscribers.forEach((cb) => {
    try {
      cb();
    } catch (_) {
      /* ignore */
    }
  });
}

function wpaClearDeferredInstallPrompt() {
  wpaDeferredInstallPrompt = null;
  wpaNotifyInstallPromptSubscribers();
}

function wpaBindGlobalInstallListenersOnce() {
  if (typeof window === 'undefined') return;
  if (window.__wpaPwaInstallBound) return;
  window.__wpaPwaInstallBound = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    if (sessionStorage.getItem('wpa_install_prompt_dismissed') === '1') return;
    wpaDeferredInstallPrompt = e;
    wpaNotifyInstallPromptSubscribers();
  });

  window.addEventListener('appinstalled', () => {
    wpaDeferredInstallPrompt = null;
    sessionStorage.setItem('wpa_install_prompt_dismissed', '1');
    wpaNotifyInstallPromptSubscribers();
  });
}

export default function WpaPwaManifestAndInstall({ companyId }) {
  const { t } = useLanguage();
  const iosShownRef = useRef(false);
  const deferredCapturedRef = useRef(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installPromptMode, setInstallPromptMode] = useState('none');
  const [deferredPromptEvent, setDeferredPromptEvent] = useState(null);

  useEffect(() => {
    if (!companyId || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker
      .register('/wpa-installability-sw.js', { scope: '/' })
      .then((reg) => console.log('[WPA-PWA] SW registered, scope:', reg.scope))
      .catch((err) => console.warn('[WPA-PWA] SW registration failed:', err));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    wpaBindGlobalInstallListenersOnce();

    if (isRunningAsInstalledPwa()) {
      console.log('[WPA-PWA] running as installed PWA (display mode), skipping install prompt');
      return;
    }

    const ua = (navigator.userAgent || '').toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);
    if (!isIOS && !isAndroid) return;

    const dismissed = () => sessionStorage.getItem('wpa_install_prompt_dismissed') === '1';

    let timer;
    let unsub = () => {};

    if (isAndroid) {
      const syncFromGlobal = () => {
        if (dismissed()) {
          setShowInstallPrompt(false);
          setDeferredPromptEvent(null);
          return;
        }
        const ev = wpaDeferredInstallPrompt;
        if (ev) {
          console.log('[WPA-PWA] ✓ showing deferred install prompt (from global store)');
          deferredCapturedRef.current = true;
          setDeferredPromptEvent(ev);
          setInstallPromptMode('android-install');
          setShowInstallPrompt(true);
        }
      };

      syncFromGlobal();
      unsub = wpaSubscribeInstallPrompt(syncFromGlobal);
    } else if (isIOS) {
      if (dismissed()) return;
      console.log('[WPA-PWA] platform: iOS — schedule install guide');
      setInstallPromptMode('ios-guide');
      timer = setTimeout(() => {
        if (dismissed()) return;
        if (iosShownRef.current) return;
        iosShownRef.current = true;
        setShowInstallPrompt(true);
      }, 1200);
    }

    return () => {
      unsub();
      if (timer) clearTimeout(timer);
    };
  }, [companyId]);

  if (!companyId || !showInstallPrompt) return null;

  const bodyText = installPromptMode === 'ios-guide'
    ? t('wpa.layout.installBodyIos')
    : t('wpa.layout.installBodyAndroid');

  const hasNativeInstall = installPromptMode === 'android-install' && deferredPromptEvent;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: Z_OVERLAY }} role="presentation" />
      <div style={{
        position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
        width: 'calc(100% - 40px)', maxWidth: 420, background: '#fff', borderRadius: 12, zIndex: Z_DIALOG, padding: 16,
      }}
      >
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{t('wpa.layout.installTitle')}</div>
        <div style={{ fontSize: 14, color: '#555', lineHeight: 1.5, marginBottom: 12 }}>{bodyText}</div>
        <div style={{ display: 'flex', gap: 10 }}>
          {hasNativeInstall && (
            <button
              type="button"
              style={{ flex: 1, border: '1px solid #ddd', background: '#fff', borderRadius: 8, padding: '10px 0', cursor: 'pointer' }}
              onClick={() => {
                setShowInstallPrompt(false);
                sessionStorage.setItem('wpa_install_prompt_dismissed', '1');
              }}
            >
              {t('wpa.layout.installLater')}
            </button>
          )}
          <button
            type="button"
            style={{ flex: 1, border: 'none', background: PRIMARY, color: '#fff', borderRadius: 8, padding: '10px 0', cursor: 'pointer', fontWeight: 600 }}
            onClick={async () => {
              if (installPromptMode === 'android-install' && deferredPromptEvent) {
                await deferredPromptEvent.prompt();
                await deferredPromptEvent.userChoice;
                wpaClearDeferredInstallPrompt();
                setDeferredPromptEvent(null);
                setShowInstallPrompt(false);
                sessionStorage.setItem('wpa_install_prompt_dismissed', '1');
                return;
              }
              setShowInstallPrompt(false);
              sessionStorage.setItem('wpa_install_prompt_dismissed', '1');
            }}
          >
            {hasNativeInstall ? t('wpa.layout.installNow') : t('wpa.layout.installGotIt')}
          </button>
        </div>
      </div>
    </>
  );
}
